import type { AgentStatus, PlaybackSpeed } from "../server/types";

type Props = {
  isRunning: boolean;
  status: AgentStatus;
  speed: PlaybackSpeed;
  model: string;
  currentToolCall: string | null;
  onStart: () => void;
  onStop: () => void;
  onSpeedChange: (speed: PlaybackSpeed) => void;
  onModelChange: (model: string) => void;
  connected: boolean;
  emulatorReady: boolean;
  hasRunCompleted: boolean;
  isExporting: boolean;
  exportProgress: string;
  videoUrl: string | null;
  onExportVideo: () => void;
  tabVideoUrl: string | null;
  publicMode: boolean;
  hasApiKey: boolean;
  seatsFull: boolean;
  agentError: string | null;
  demoAvailable: boolean;
  isDemoPlaying: boolean;
  demoProgress: string;
  onWatchDemo: () => void;
  recordTab: boolean;
  onRecordTabChange: (v: boolean) => void;
};

const speeds: PlaybackSpeed[] = ["1x", "2x", "4x", "instant"];
const models = [
  { value: "claude-sonnet-4-6", label: "SONNET 4.6" },
  { value: "claude-opus-4-6", label: "OPUS 4.6" },
];

function getStatusDisplay(
  isRunning: boolean,
  status: AgentStatus,
  currentToolCall: string | null
): { text: string; color: string; showSpinner: boolean } {
  if (!isRunning) {
    return { text: "IDLE", color: "text-nes-gray", showSpinner: false };
  }

  switch (status) {
    case "thinking":
      return {
        text: "AI THINKING...",
        color: "text-nes-gold",
        showSpinner: true,
      };
    case "executing":
      return {
        text: currentToolCall
          ? `RUNNING: ${currentToolCall}`
          : "EMULATOR WORKING...",
        color: "text-nes-green",
        showSpinner: true,
      };
    default:
      return { text: "RUNNING", color: "text-nes-blue", showSpinner: true };
  }
}

export function ControlPanel({
  isRunning,
  status,
  speed,
  model,
  currentToolCall,
  onStart,
  onStop,
  onSpeedChange,
  onModelChange,
  connected,
  emulatorReady,
  hasRunCompleted,
  isExporting,
  exportProgress,
  videoUrl,
  onExportVideo,
  tabVideoUrl,
  publicMode,
  hasApiKey,
  seatsFull,
  agentError,
  demoAvailable,
  isDemoPlaying,
  demoProgress,
  onWatchDemo,
  recordTab,
  onRecordTabChange,
}: Props) {
  const canStart = connected && emulatorReady && !isRunning;
  const startDisabled =
    !canStart || (publicMode && !hasApiKey) || isDemoPlaying || seatsFull;
  const statusDisplay = getStatusDisplay(isRunning, status, currentToolCall);

  return (
    <div className="nes-panel">
      <div className="nes-panel-header">CONTROLS</div>
      <div className="p-3 space-y-3">
        {/* Prominent status bar */}
        <div
          className={`font-pixel text-xs px-3 py-2 border-2 ${
            isRunning ? "border-nes-border" : "border-nes-border/30"
          } flex items-center gap-2`}
        >
          {statusDisplay.showSpinner && (
            <span className="nes-spinner" />
          )}
          <span className={statusDisplay.color}>{statusDisplay.text}</span>
        </div>

        {/* Start/Stop */}
        <div className="flex gap-2">
          <button
            onClick={onStart}
            disabled={startDisabled}
            className="nes-btn nes-btn-green flex-1"
          >
            ▶ START
          </button>
          <button
            onClick={onStop}
            disabled={!isRunning}
            className="nes-btn nes-btn-red flex-1"
          >
            ■ STOP
          </button>
          {isDemoPlaying ? (
            <button disabled className="nes-btn flex-1">
              DEMO PLAYING...
            </button>
          ) : (
            demoAvailable &&
            !isRunning && (
              <button onClick={onWatchDemo} className="nes-btn flex-1">
                ▶ WATCH DEMO
              </button>
            )
          )}
        </div>
        {isDemoPlaying && demoProgress && (
          <div className="font-pixel text-[10px] text-nes-gold">
            {demoProgress}
          </div>
        )}

        {/* Status messages */}
        {agentError ? (
          <div className="font-pixel text-[10px] text-nes-red leading-relaxed">
            {agentError}
          </div>
        ) : publicMode && !hasApiKey ? (
          <div className="font-pixel text-[10px] text-nes-gold">
            ADD YOUR API KEY IN THE KEY PANEL →
          </div>
        ) : seatsFull ? (
          <div className="font-pixel text-[10px] text-nes-gold">
            ALL SEATS BUSY — TRY AGAIN SOON
          </div>
        ) : null}

        {/* Speed */}
        <div>
          <div className="font-pixel text-xs text-nes-gray mb-1">SPEED:</div>
          <div className="flex gap-1">
            {speeds.map((s) => (
              <button
                key={s}
                onClick={() => onSpeedChange(s)}
                className={`nes-btn text-xs flex-1 ${
                  speed === s ? "nes-btn-active" : ""
                }`}
              >
                {s === "instant" ? ">>" : s}
              </button>
            ))}
            <button
              onClick={() => onRecordTabChange(!recordTab)}
              disabled={isRunning}
              title="Record this tab while Claude plays (browser will ask for screen share)"
              className={`nes-btn text-xs ${recordTab ? "nes-btn-active" : ""}`}
            >
              {recordTab ? "REC TAB: ON" : "REC TAB: OFF"}
            </button>
          </div>
        </div>

        {/* Model */}
        <div>
          <div className="font-pixel text-xs text-nes-gray mb-1">MODEL:</div>
          <div className="flex gap-1">
            {models.map((m) => (
              <button
                key={m.value}
                onClick={() => onModelChange(m.value)}
                disabled={isRunning}
                className={`nes-btn text-xs flex-1 ${
                  model === m.value ? "nes-btn-active" : ""
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Export Video */}
        {!isRunning && (
          <div className="space-y-2">
            <button
              onClick={onExportVideo}
              disabled={isExporting}
              className="nes-btn nes-btn-green w-full text-xs"
            >
              {isExporting ? "RECORDING..." : "EXPORT VIDEO"}
            </button>
            {exportProgress && (
              <div className="font-pixel text-xs text-nes-gold flex items-center gap-2">
                {isExporting && <span className="nes-spinner" />}
                {exportProgress}
              </div>
            )}
            {videoUrl && (
              <a
                href={videoUrl}
                download="nestas-replay.webm"
                className="nes-btn w-full text-xs text-center block"
              >
                DOWNLOAD REPLAY .WEBM
              </a>
            )}
            {tabVideoUrl && (
              <a
                href={tabVideoUrl}
                download="nestas-session.webm"
                className="nes-btn w-full text-xs text-center block"
              >
                DOWNLOAD SESSION .WEBM
              </a>
            )}
          </div>
        )}

        {/* Connection status */}
        <div className="font-pixel text-xs flex items-center gap-2">
          <span
            className={connected ? "text-nes-green" : "text-nes-red"}
          >
            {connected ? "● CONNECTED" : "● DISCONNECTED"}
          </span>
        </div>
      </div>
    </div>
  );
}
